import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { priceListsApi, studiesApi, studyPricesApi, profilesApi, profilePricesApi } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, Plus, Pencil, Trash2, Search, Save, FlaskConical, Layers } from "lucide-react";

export default function PriceLists() {
  const [priceLists, setPriceLists] = useState([]);
  const [studies, setStudies] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [studyPrices, setStudyPrices] = useState([]);
  const [profilePrices, setProfilePrices] = useState([]);
  const [selectedPriceList, setSelectedPriceList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [activeTab, setActiveTab] = useState("studies");
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    is_default: false,
    active: true,
  });
  const [priceChanges, setPriceChanges] = useState({});
  const [profilePriceChanges, setProfilePriceChanges] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedPriceList) {
      fetchStudyPrices(selectedPriceList);
      fetchProfilePrices(selectedPriceList);
    }
  }, [selectedPriceList]);

  const fetchData = async () => {
    try {
      const [priceListsRes, studiesRes, profilesRes] = await Promise.all([
        priceListsApi.getAll(),
        studiesApi.getAll(),
        profilesApi.getAll(),
      ]);
      setPriceLists(priceListsRes.data);
      setStudies(studiesRes.data);
      setProfiles(profilesRes.data);
      
      // Auto-select default list
      const defaultList = priceListsRes.data.find(p => p.is_default);
      if (defaultList) {
        setSelectedPriceList(defaultList.id);
      } else if (priceListsRes.data.length > 0) {
        setSelectedPriceList(priceListsRes.data[0].id);
      }
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudyPrices = async (priceListId) => {
    try {
      const response = await studyPricesApi.getAll(priceListId);
      setStudyPrices(response.data);
      setPriceChanges({});
    } catch (error) {
      console.error("Error loading study prices:", error);
    }
  };

  const fetchProfilePrices = async (priceListId) => {
    try {
      const response = await profilePricesApi.getAll(priceListId);
      setProfilePrices(response.data);
      setProfilePriceChanges({});
    } catch (error) {
      console.error("Error loading profile prices:", error);
    }
  };

  const handleOpenDialog = (list = null) => {
    if (list) {
      setEditingList(list);
      setFormData({
        name: list.name,
        code: list.code,
        description: list.description || "",
        is_default: list.is_default,
        active: list.active,
      });
    } else {
      setEditingList(null);
      setFormData({
        name: "",
        code: "",
        description: "",
        is_default: false,
        active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingList) {
        await priceListsApi.update(editingList.id, formData);
        toast.success("Lista de precios actualizada");
      } else {
        await priceListsApi.create(formData);
        toast.success("Lista de precios creada");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar lista de precios");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Está seguro de eliminar esta lista de precios?")) return;
    try {
      await priceListsApi.delete(id);
      toast.success("Lista de precios eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Study price handlers
  const handleStudyPriceChange = (studyId, price) => {
    setPriceChanges(prev => ({
      ...prev,
      [studyId]: price
    }));
  };

  const getStudyPrice = (studyId) => {
    if (priceChanges[studyId] !== undefined) {
      return priceChanges[studyId];
    }
    const priceRecord = studyPrices.find(p => p.study_id === studyId);
    return priceRecord?.price || "";
  };

  const saveStudyPrices = async () => {
    try {
      const entries = Object.entries(priceChanges);
      for (const [studyId, price] of entries) {
        if (price !== "" && price !== null) {
          await studyPricesApi.set({
            study_id: studyId,
            price_list_id: selectedPriceList,
            price: parseFloat(price) || 0
          });
        }
      }
      toast.success("Precios de estudios guardados");
      fetchStudyPrices(selectedPriceList);
    } catch (error) {
      console.error("Error saving study prices:", error);
      toast.error("Error al guardar precios de estudios");
    }
  };

  // Profile price handlers
  const handleProfilePriceChange = (profileId, price) => {
    setProfilePriceChanges(prev => ({
      ...prev,
      [profileId]: price
    }));
  };

  const getProfilePrice = (profileId) => {
    if (profilePriceChanges[profileId] !== undefined) {
      return profilePriceChanges[profileId];
    }
    const priceRecord = profilePrices.find(p => p.profile_id === profileId);
    return priceRecord?.price || "";
  };

  const saveProfilePrices = async () => {
    try {
      const entries = Object.entries(profilePriceChanges);
      for (const [profileId, price] of entries) {
        if (price !== "" && price !== null) {
          await profilePricesApi.set({
            profile_id: profileId,
            price_list_id: selectedPriceList,
            price: parseFloat(price) || 0
          });
        }
      }
      toast.success("Precios de perfiles guardados");
      fetchProfilePrices(selectedPriceList);
    } catch (error) {
      console.error("Error saving profile prices:", error);
      toast.error("Error al guardar precios de perfiles");
    }
  };

  // Filter studies
  const filteredStudies = studies.filter(
    (study) =>
      study.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      study.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter profiles
  const filteredProfiles = profiles.filter(
    (profile) =>
      profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasPriceChanges = Object.keys(priceChanges).length > 0;
  const hasProfilePriceChanges = Object.keys(profilePriceChanges).length > 0;

  return (
    <div className="space-y-6" data-testid="price-lists-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            Listas de Precios
          </h1>
          <p className="text-muted-foreground">
            Administre las listas de precios para estudios y perfiles
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-price-list-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Lista
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Price Lists Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5" />
              Listas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Cargando...</div>
            ) : (
              <div className="space-y-2">
                {priceLists.map((list) => (
                  <div
                    key={list.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedPriceList === list.id
                        ? "bg-primary/10 border-primary"
                        : "hover:bg-muted"
                    }`}
                    onClick={() => setSelectedPriceList(list.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{list.name}</p>
                        <p className="text-xs text-muted-foreground">{list.code}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(list);
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(list.id);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    {list.is_default && (
                      <Badge variant="secondary" className="mt-2 text-xs">
                        Predeterminada
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prices Panel */}
        <Card className="lg:col-span-3">
          <CardHeader className="space-y-4">
            <CardTitle className="text-lg">
              {priceLists.find(p => p.id === selectedPriceList)?.name || "Seleccione una lista"}
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar estudio o perfil..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent>
            {selectedPriceList ? (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="studies" className="flex items-center gap-2">
                    <FlaskConical className="h-4 w-4" />
                    Estudios ({filteredStudies.length})
                  </TabsTrigger>
                  <TabsTrigger value="profiles" className="flex items-center gap-2">
                    <Layers className="h-4 w-4" />
                    Perfiles ({filteredProfiles.length})
                  </TabsTrigger>
                </TabsList>

                {/* Studies Tab */}
                <TabsContent value="studies">
                  <div className="space-y-4">
                    <div className="max-h-[400px] overflow-y-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Código</TableHead>
                            <TableHead>Estudio</TableHead>
                            <TableHead className="w-32">Precio</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredStudies.map((study) => (
                            <TableRow key={study.id}>
                              <TableCell className="font-mono">{study.code}</TableCell>
                              <TableCell>{study.name}</TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={getStudyPrice(study.id)}
                                  onChange={(e) => handleStudyPriceChange(study.id, e.target.value)}
                                  className="h-8 w-28"
                                  placeholder="0.00"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {hasPriceChanges && (
                      <div className="flex justify-end">
                        <Button onClick={saveStudyPrices}>
                          <Save className="h-4 w-4 mr-2" />
                          Guardar Precios de Estudios
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Profiles Tab */}
                <TabsContent value="profiles">
                  <div className="space-y-4">
                    {filteredProfiles.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No hay perfiles creados. Crea perfiles en Catálogos → Perfiles.
                      </div>
                    ) : (
                      <div className="max-h-[400px] overflow-y-auto border rounded-lg">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Código</TableHead>
                              <TableHead>Perfil</TableHead>
                              <TableHead>Estudios incluidos</TableHead>
                              <TableHead className="w-32">Precio</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredProfiles.map((profile) => (
                              <TableRow key={profile.id}>
                                <TableCell className="font-mono">{profile.code}</TableCell>
                                <TableCell className="font-medium">{profile.name}</TableCell>
                                <TableCell>
                                  <Badge variant="secondary">
                                    {profile.study_ids?.length || 0} estudios
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={getProfilePrice(profile.id)}
                                    onChange={(e) => handleProfilePriceChange(profile.id, e.target.value)}
                                    className="h-8 w-28"
                                    placeholder="0.00"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                    
                    {hasProfilePriceChanges && (
                      <div className="flex justify-end">
                        <Button onClick={saveProfilePrices}>
                          <Save className="h-4 w-4 mr-2" />
                          Guardar Precios de Perfiles
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Seleccione una lista de precios para editar
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingList ? "Editar Lista de Precios" : "Nueva Lista de Precios"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, code: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.is_default}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, is_default: checked }))
                  }
                />
                <Label>Lista predeterminada</Label>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingList ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
